import { useCallback, useState } from 'react';
import { Alert, Box, Button, Divider, Grid, Paper, Stack, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Link as RouterLink } from 'react-router-dom';
import { InvestmentDTO } from '@inversor-hub/types';
import { useGetMe } from '@/lib/api/me';
import { useGetMyCoInvestorContract } from '@/lib/api/investments';
import { CoInvestorContractModal } from '@/features/investments/co-investment/CoInvestorContractModal';
import { CollaboratorSection } from '@/features/investments/CollaboratorSection';
import { RealPerformanceBand } from '@/features/investments/performance';
import { SectionHeading } from '../shared/SectionHeading';
import { cardSurface } from '../shared/surfaces';
import { CapexBreakdown } from './overview/CapexBreakdown';
import { ForecastCards } from './overview/ForecastCards';
import { ParticipationCard } from './overview/ParticipationCard';
import { PropertyDetails } from './overview/PropertyDetails';

interface OverviewTabProps {
  investment: InvestmentDTO;
}

export const OverviewTab = ({ investment }: OverviewTabProps) => {
  const [contractModalOpen, setContractModalOpen] = useState(false);
  const { data: me } = useGetMe();

  const openContract = useCallback(() => setContractModalOpen(true), []);
  const closeContract = useCallback(() => setContractModalOpen(false), []);

  const ownershipPct = investment.myOwnershipPercentage;
  const isCoInvestment = investment.isCoInvestment && ownershipPct !== undefined;
  const canViewMyContract = me?.role === 'investor' && isCoInvestment;
  const canViewParticipantSection =
    me?.role === 'investor'
      ? Boolean(investment.psi)
      : me?.role === 'psi' && !investment.isCoInvestment;

  const { data: myContract } = useGetMyCoInvestorContract(
    canViewMyContract ? investment.id : undefined
  );

  const currentYear = new Date().getUTCFullYear();

  return (
    <Stack spacing={4}>
      {isCoInvestment ? (
        <ParticipationCard investment={investment} ownershipPct={ownershipPct} />
      ) : null}

      {canViewMyContract && myContract ? (
        <Paper elevation={0} sx={{ p: 3, ...cardSurface }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Contrato de co-inversión
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {myContract?.status === 'NOT_GENERATED' ? (
            <Alert severity="info">Tu contrato aún no ha sido generado por el PSI.</Alert>
          ) : null}

          {myContract?.status === 'PENDING_SIGNATURE' ? (
            <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
              <Typography variant="body2" color="text.secondary">
                Tu contrato está pendiente de firma.
              </Typography>
              <Button variant="contained" onClick={openContract}>
                Ver y firmar contrato
              </Button>
            </Box>
          ) : null}

          {myContract?.status === 'SIGNED' ? (
            <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
              <Typography variant="body2" color="text.secondary">
                El contrato ya está firmado.
              </Typography>
              <Box display="flex" gap={1}>
                <Button variant="outlined" onClick={openContract}>
                  Ver contrato
                </Button>
                <Button
                  variant="contained"
                  onClick={() =>
                    myContract.url && window.open(myContract.url, '_blank', 'noopener,noreferrer')
                  }
                  disabled={!myContract.url}
                >
                  Descargar contrato
                </Button>
              </Box>
            </Box>
          ) : null}
        </Paper>
      ) : null}

      <Box component="section">
        <SectionHeading
          step={1}
          title={`Cómo va la inversión en ${currentYear}`}
          description="Lo que ha pasado de verdad, según los cobros y pagos que tienes registrados."
          action={
            <Button
              size="small"
              component={RouterLink}
              to={`/inversiones/${investment.id}?tab=performance`}
              endIcon={<ArrowForwardIcon />}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Ver rentabilidad
            </Button>
          }
        />
        <RealPerformanceBand investmentId={investment.id} />
      </Box>

      <Box component="section">
        <SectionHeading
          step={2}
          title="Lo que esperabas al comprar"
          description="Referencia calculada con los datos de la ficha, no con los movimientos reales."
        />
        <ForecastCards investment={investment} />
      </Box>

      <Box component="section">
        <SectionHeading
          step={3}
          title="La inversión y el inmueble"
          description="Cuánto pusiste, en qué se repartió y los datos de la propiedad."
        />
        <Grid container spacing={3}>
          <Grid item xs={12} md={7}>
            <CapexBreakdown investment={investment} />
          </Grid>
          <Grid item xs={12} md={5}>
            <PropertyDetails investment={investment} />
          </Grid>
        </Grid>
      </Box>

      {canViewParticipantSection && me?.role ? (
        <CollaboratorSection investment={investment} viewerRole={me.role} />
      ) : null}

      {canViewMyContract && myContract ? (
        <CoInvestorContractModal
          open={contractModalOpen}
          onClose={closeContract}
          investmentId={investment.id}
          contractStatus={myContract.status}
          contractUrl={myContract.url}
          hasUserSignature={Boolean(me?.signature?.trim())}
        />
      ) : null}
    </Stack>
  );
};
